import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useTranslation } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { deviceIdService } from '@/core/services/deviceIdService';
import { passcodeStorage } from '@/core/storage/passcodeStorage';
import { useAppStore } from '@/core/stores/appStore';
import { encryptedStorage } from '@/core/wrappers/encryptedStorage';
import { shouldInvokeSkipPasscodeApi } from '@/modules/passcode/utils/skipPasscodeGate';
import { useCallback, useState } from 'react';
import { passcodeService } from '../services/passcodeService';

/**
 * Custom hook for passcode business logic
 * Handles passcode operations, state management, and storage
 */
export const usePasscode = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();
    const user = useAppStore((state) => state.auth.user);

    /**
     * Setup passcode for the user
     */
    const setupPasscode = useCallback(
        async (passcode: string, biometricEnabled: boolean = false): Promise<void> => {
            try {
                setIsLoading(true);
                setError(null);

                // Get device ID
                const deviceId = await deviceIdService.getDeviceId();

                logger.info('usePasscode:Setting up passcode', { biometricEnabled, deviceId });

                // 1. Call API service (customer_id is not sent in body, backend gets it from token)
                const response = await passcodeService.setPasscode(
                    passcode,
                    biometricEnabled,
                    deviceId
                );

                if (response.status !== 'success') {
                    throw new Error(response.message || 'Failed to set passcode');
                }

                // 2. Save state to secure storage (passcode is now set)
                await passcodeStorage.setPasscodeState(biometricEnabled);
                // Clear setup required flag since passcode is now set
                await passcodeStorage.clearSetupRequired();

                // 3. If biometric is enabled, store passcode for biometric login
                if (biometricEnabled) {
                    await passcodeStorage.setPasscode(passcode);
                    logger.info('usePasscode:Passcode stored for biometric login');
                }

                logger.info('usePasscode:Passcode set successfully', { biometricEnabled });
                trackCatalogEvent({
                    screen: DT_SCREEN.PASSCODE,
                    actionName: 'passcode_set_success',
                    eventType: 'action',
                });
            } catch (err) {
                trackCatalogEvent({
                    screen: DT_SCREEN.PASSCODE,
                    actionName: 'passcode_set_error',
                    eventType: 'error',
                });
                const errorMessage = err instanceof Error ? err.message : t('profile.passcodeSetError');
                setError(errorMessage);
                logger.error('usePasscode:Failed to set passcode', err);
                throw new Error(errorMessage);
            } finally {
                setIsLoading(false);
            }
        },
        [user, t]
    );

    /**
     * Verify passcode
     * Note: Token handling removed - backend implementation is incomplete
     * This only verifies the passcode and returns success/failure
     * TODO: Add customer_id parameter after UAE pass implementations
     */
    const verifyPasscode = useCallback(
        async (passcode: string): Promise<{ success: boolean }> => {
            try {
                setIsLoading(true);
                setError(null);

                // TODO: Add customer_id retrieval and passing after UAE pass implementations
                // Currently not sending customer_id to verify-passcode API
                // let customerId: string | undefined = user?.customer_id;
                // if (!customerId) {
                //     const storedCustomerId = await passcodeStorage.getCustomerId();
                //     customerId = storedCustomerId || undefined;
                // }
                // if (!customerId) {
                //     throw new Error('User ID not available');
                // }

                // Get device ID
                const deviceId = await deviceIdService.getDeviceId();

                logger.info('usePasscode:Verifying passcode', { deviceId });

                // Call API service (without customer_id for now)
                const response = await passcodeService.verifyPasscode(passcode, deviceId);

                if (response.status !== 'success') {
                    trackCatalogEvent({
                        screen: DT_SCREEN.PASSCODE,
                        actionName: 'passcode_verify_error',
                        eventType: 'error',
                    });
                    throw new Error(response.message || 'Failed to verify passcode');
                }

                logger.info('usePasscode:Passcode verified successfully');

                // Store accessToken from response
                if (response.accessToken) {
                    const setPasscodeToken = useAppStore.getState().setPasscodeToken;
                    setPasscodeToken(response.accessToken);
                    logger.info('usePasscode:Passcode token stored');
                }

                // On fresh login (no local passcode data), save passcode state to local storage
                // This ensures on app restart, passcode login screen appears
                const isPasscodeSetLocally = await passcodeStorage.isPasscodeSet();
                if (!isPasscodeSetLocally) {
                    logger.info('usePasscode:Fresh login detected - saving passcode state to local storage');
                    try {
                        // Get biometric enabled state (if available from previous session)
                        // For fresh login, default to false
                        const biometricEnabled = await passcodeStorage.getBiometricEnabled();
                        await passcodeStorage.setPasscodeState(biometricEnabled);
                        logger.info('usePasscode:Passcode state saved to local storage for future restarts');
                    } catch (saveError) {
                        logger.warn('usePasscode:Failed to save passcode state to local storage', saveError);
                        // Don't fail verification if storage save fails
                    }
                }

                // Try to restore user data from storage if not already in store
                const currentUser = useAppStore.getState().auth.user;
                if (!currentUser) {
                    try {
                        const appState = await encryptedStorage.getDecrypted<any>('app.state');
                        const storedUser = appState?.auth?.user;
                        if (storedUser) {
                            const setUser = useAppStore.getState().setUser;
                            setUser(storedUser);
                            logger.info('usePasscode:User data restored from storage');
                        }
                    } catch (restoreError) {
                        logger.warn('usePasscode:Failed to restore user data', restoreError);
                    }
                }

                trackCatalogEvent({
                    screen: DT_SCREEN.PASSCODE,
                    actionName: 'passcode_verify_success',
                    eventType: 'action',
                });
                return { success: true };
            } catch (err) {
                trackCatalogEvent({
                    screen: DT_SCREEN.PASSCODE,
                    actionName: 'passcode_verify_error',
                    eventType: 'error',
                });
                const errorMessage = err instanceof Error ? err.message : t('profile.passcodeVerifyError');
                setError(errorMessage);
                logger.error('usePasscode:Failed to verify passcode', err);
                return {
                    success: false,
                };
            } finally {
                setIsLoading(false);
            }
        },
        [user, t]
    );

    /**
     * Change passcode
     */
    const changePasscode = useCallback(
        async (currentPasscode: string, newPasscode: string): Promise<void> => {
            try {
                setIsLoading(true);
                setError(null);

                logger.info('usePasscode:Changing passcode');

                // 1. Call API service
                const response = await passcodeService.changePasscode(
                    currentPasscode,
                    newPasscode
                );

                if (response.status !== 'success') {
                    throw new Error(response.message || 'Failed to change passcode');
                }

                // 2. Update storage (passcode is still set, just changed)
                const biometricEnabled = await passcodeStorage.getBiometricEnabled();
                await passcodeStorage.setPasscodeState(biometricEnabled);

                // 3. If biometric is enabled, update stored passcode
                if (biometricEnabled) {
                    await passcodeStorage.setPasscode(newPasscode);
                    logger.info('usePasscode:Stored passcode updated for biometric');
                } else {
                    // If biometric is disabled, clear stored passcode
                    await passcodeStorage.clearPasscode();
                }

                logger.info('usePasscode:Passcode changed successfully');
            } catch (err) {
                let errorMessage: string;

                if (err instanceof Error && /invalid passcode/i.test(err.message)) {
                    // Backend indicates old/current passcode is wrong
                    errorMessage = t('profile.passcodeOldInvalid');
                } else {
                    errorMessage =
                        err instanceof Error ? err.message : t('profile.passcodeChangeError');
                }

                setError(errorMessage);
                logger.error('usePasscode:Failed to change passcode', err);
                throw new Error(errorMessage);
            } finally {
                setIsLoading(false);
            }
        },
        [user, t]
    );

    /**
     * Skip passcode setup — email/password login only.
     * No-op for UAE Pass / Emirates ID / ADHA; never hits the API for those.
     */
    const skipPasscode = useCallback(async (from: string = 'login'): Promise<void> => {
        if (!shouldInvokeSkipPasscodeApi()) {
            logger.info('usePasscode:Skip passcode ignored — not email/password login', { from });
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            // Get device ID
            const deviceId = await deviceIdService.getDeviceId();

            logger.info('usePasscode:Skipping passcode', { deviceId, from });

            // 1. Call API service
            const response = await passcodeService.skipPasscode(deviceId, from);

            if (response.status !== 'success') {
                throw new Error(response.message || 'Failed to skip passcode');
            }

            // 2. Store accessToken from response if provided
            if (response.accessToken) {
                const setPasscodeToken = useAppStore.getState().setPasscodeToken;
                setPasscodeToken(response.accessToken);
                logger.info('usePasscode:Skip passcode token stored');
            }

            // 3. Clear storage state
            await passcodeStorage.clearPasscodeState();

            logger.info('usePasscode:Passcode skipped successfully');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : t('profile.passcodeSkipError');
            setError(errorMessage);
            logger.error('usePasscode:Failed to skip passcode', err);
            throw new Error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [user, t]);

    /**
     * Reset passcode (forgot passcode)
     * Calls API to reset passcode flags on backend
     */
    const resetPasscode = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
        try {
            setIsLoading(true);
            setError(null);

            logger.info('usePasscode:Resetting passcode');

            // Call API service
            const response = await passcodeService.forgotPasscode();

            if (response.status !== 'success') {
                throw new Error(response.message || 'Failed to reset passcode');
            }

            // Clear local passcode storage
            await passcodeStorage.clearPasscodeState();

            logger.info('usePasscode:Passcode reset successfully', { message: response.message });
            return { success: true, message: response.message };
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : t('profile.passcodeResetError');
            setError(errorMessage);
            logger.error('usePasscode:Failed to reset passcode', err);
            return { success: false, message: errorMessage };
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    /**
     * Check if passcode is set
     */
    const checkPasscodeState = useCallback(async (): Promise<boolean> => {
        try {
            return await passcodeStorage.isPasscodeSet();
        } catch (error) {
            logger.error('usePasscode:Failed to check passcode state', error);
            return false;
        }
    }, []);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        // State
        isLoading,
        error,

        // Actions
        setupPasscode,
        verifyPasscode,
        changePasscode,
        skipPasscode,
        resetPasscode,
        checkPasscodeState,
        clearError,
    };
};

